<script lang="ts">
  import { onMount } from "svelte"
  import { createEventDispatcher } from "svelte"

  interface AnalyticsData {
    students?: number
    currentRate?: number
    desiredRate?: number
    placementIncrease?: number
    roiMultiplier?: number
    deltaPlacement?: number
    deltaSalary?: number
  }

  const dispatch = createEventDispatcher()

  // ROI Calculator state (exact from prototype)
  let students = $state(100)
  let currentRate = $state(80)
  let desiredRate = $state(88)
  let averageSalary = $state(100000)

  const costPerStudent = 99

  // Calculate values using $derived (exact from prototype logic)
  const placementIncrease = $derived(desiredRate - currentRate) // percentage points increase
  const deltaPlacement = $derived(
    Math.round((placementIncrease / 100) * students),
  ) // additional students placed, rounded
  const deltaSalary = $derived(deltaPlacement * averageSalary) // additional earnings
  const totalProgramCost = $derived(students * costPerStudent) // total investment
  const roiMultiplier = $derived(Math.round(deltaSalary / totalProgramCost))

  // Analytics tracking
  const trackCalculatorUsage = (action: string, data: AnalyticsData) => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", action, {
        event_category: "University ROI Calculator",
        event_label: "university_calculator_interaction",
        value: data.roiMultiplier || 0,
        custom_parameters: {
          students: data.students,
          placement_increase: data.placementIncrease,
          roi_multiplier: data.roiMultiplier,
        },
      })
    }
  }

  // Track usage when values change significantly
  $effect(() => {
    if (roiMultiplier > 0) {
      trackCalculatorUsage("calculate_university_roi", {
        students,
        placementIncrease,
        roiMultiplier,
      })
    }
  })

  onMount(() => {
    trackCalculatorUsage("university_calculator_loaded", {
      students,
      currentRate,
      desiredRate,
    })
  })

  const handleConsultationRequest = () => {
    trackCalculatorUsage("request_partnership_consultation", {
      students,
      roiMultiplier,
      deltaPlacement,
      deltaSalary,
    })
    dispatch("consultationRequested", {
      calculatorData: {
        students,
        currentRate,
        desiredRate,
        averageSalary,
        roiMultiplier,
        deltaPlacement,
        deltaSalary,
        totalProgramCost,
      },
    })
  }
</script>

<div class="w-full max-w-2xl mx-auto">
  <div class="card bg-white border-2 border-primary shadow-xl">
    <div class="card-body">
      <div class="pb-4">
        <h3 class="card-title text-accent-content">
          Calculate Your University's Return on Investment (ROI)
        </h3>
        <p class="text-gray-600">
          See the impact of AceTheRound on your program
        </p>
      </div>

      <div class="space-y-6">
        <div class="space-y-5">
          <!-- Number of Students Input -->
          <div>
            <label
              for="students"
              class="block text-sm font-medium text-accent-content mb-1"
            >
              Number of Finance-Focused Students
            </label>
            <input
              id="students"
              type="number"
              bind:value={students}
              min="1"
              class="input input-bordered w-full bg-white text-accent-content"
            />
          </div>

          <!-- Current Placement Rate Slider -->
          <div>
            <label
              for="currentRate"
              class="block text-sm font-medium text-accent-content mb-1"
            >
              Current Placement Rate: <span class="font-semibold"
                >{currentRate}%</span
              >
            </label>
            <input
              id="currentRate"
              type="range"
              bind:value={currentRate}
              min="0"
              max="100"
              step="1"
              class="range range-primary w-full"
            />
            <div class="w-full flex justify-between text-xs px-2 text-gray-500">
              <span>0%</span>
              <span>50%</span>
              <span>100%</span>
            </div>
          </div>

          <!-- Desired Placement Rate Slider -->
          <div>
            <label
              for="desiredRate"
              class="block text-sm font-medium text-accent-content mb-1"
            >
              Desired Placement Rate: <span class="font-semibold"
                >{desiredRate}%</span
              >
            </label>
            <input
              id="desiredRate"
              type="range"
              bind:value={desiredRate}
              min="0"
              max="100"
              step="1"
              class="range range-primary w-full"
            />
            <div class="w-full flex justify-between text-xs px-2 text-gray-500">
              <span>0%</span>
              <span>50%</span>
              <span>100%</span>
            </div>
          </div>

          <!-- Average Salary Input -->
          <div>
            <label
              for="averageSalary"
              class="block text-sm font-medium text-accent-content mb-1"
            >
              Average Salary of Placed Graduates
            </label>
            <input
              id="averageSalary"
              type="number"
              bind:value={averageSalary}
              min="0"
              step="1000"
              class="input input-bordered w-full bg-white text-accent-content"
            />
          </div>
        </div>

        <!-- Calculation Breakdown (exact from prototype) -->
        <div class="space-y-4 p-5 bg-gray-50 rounded-lg">
          <h3 class="text-lg font-bold text-secondary">
            Calculation Breakdown
          </h3>
          <ol class="list-decimal pl-5 space-y-3 text-md text-accent-content">
            <li>
              <strong>Placement Rate Increase:</strong>
              {desiredRate}% - {currentRate}% =
              <strong>{placementIncrease}% increase</strong>
            </li>
            <li>
              <strong>Additional Students Placed:</strong>
              {placementIncrease}% x {students} students =
              <strong>{deltaPlacement} students</strong>
            </li>
            <li>
              <strong>Additional First-Year Earnings:</strong>
              {deltaPlacement} students × ${averageSalary.toLocaleString()} =
              <strong>${deltaSalary.toLocaleString()}</strong>
            </li>
            <li>
              <strong>Monthly Program Investment:</strong>
              {students} students × ${costPerStudent} per student =
              <strong>${totalProgramCost.toLocaleString()}</strong>
            </li>
            <li>
              <strong>Return on Investment (ROI) Multiplier:</strong>
              ${deltaSalary.toLocaleString()} ÷ ${totalProgramCost.toLocaleString()}
              = <strong>{roiMultiplier}x</strong>
            </li>
          </ol>
        </div>

        <!-- ROI Results (exact from prototype) -->
        <div class="bg-primary p-5 rounded-md text-white">
          <p class="text-2xl font-bold mb-2">
            Return on Investment: <span class="underline">{roiMultiplier}x</span
            >
          </p>
          <p class="text-lg">
            That's a <span class="font-bold"
              >${deltaSalary.toLocaleString()}</span
            >
            return on your
            <span class="font-bold">${totalProgramCost.toLocaleString()}</span> monthly
            investment!
          </p>
        </div>
      </div>

      <!-- CTA Button (exact from prototype) -->
      <div class="card-actions justify-end pt-4">
        <button
          class="btn w-full bg-accent hover:bg-yellow-500 text-accent-content text-lg font-semibold border-none"
          onclick={handleConsultationRequest}
        >
          Schedule a Partnership Consultation
        </button>
      </div>
    </div>
  </div>
</div>

<style>
  .range-primary::-webkit-slider-thumb {
    background-color: var(--color-primary);
  }

  .range-primary::-moz-range-thumb {
    background-color: var(--color-primary);
    border: none;
  }
</style>
